import { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { motion } from "framer-motion";
import { Loader2, Briefcase, AlertCircle, Mail, KeyRound, ArrowRight, Globe, Check, Info } from "lucide-react";
import { CountryCombobox } from "@/components/ui/country-combobox";
import { useToast } from "@/hooks/use-toast";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import LanguageSwitcher from "@/components/layout/LanguageSwitcher";
import { getTranslatedNaceSectorOptions } from "@/constants/naceSectors";
import { getPasswordStrength, type PasswordStrength } from "@/lib/password-strength";
import bgDecorative from "@/assets/onboarding/bg-decorative.svg";
import logo from "@/assets/Logo.png";

interface InvitationData {
  valid: boolean;
  role: string;
  companyName: string;
  sector?: string | null;
  country?: string | null;
  workspaceId?: string | null;
  // Set when the invite was sent by a portfolio manager, who gets read-only access to the company.
  portfolioAccess?: { managerName: string; organization: string } | null;
}

const strengthConfig: Record<Exclude<PasswordStrength, "empty">, { color: string; width: string; segments: number }> = {
  weak: { color: "bg-red-500", width: "w-1/3", segments: 1 },
  medium: { color: "bg-yellow-500", width: "w-2/3", segments: 2 },
  strong: { color: "bg-green-500", width: "w-full", segments: 3 },
};

const passwordStrengthLabels: Record<Exclude<PasswordStrength, "empty">, string> = {
  weak: "Weak",
  medium: "Medium",
  strong: "Strong",
};

export default function Onboarding() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const naceOptions = getTranslatedNaceSectorOptions();
  const [isLoading, setIsLoading] = useState(false);
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null);
  const [verifyingToken, setVerifyingToken] = useState(true);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [invitationToken, setInvitationToken] = useState<string | null>(null);

  // Form fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState("");
  const [sector, setSector] = useState("");
  const [acceptPolicy, setAcceptPolicy] = useState(false);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [privacyPolicyContent, setPrivacyPolicyContent] = useState<string>("");
  const policyVersion = "v1";
  const isWorkspaceCreation = !invitationData?.workspaceId;

  const passwordStrength = useMemo(() => getPasswordStrength(password), [password]);
  const passwordStrengthMeta = passwordStrength === "empty" ? null : strengthConfig[passwordStrength];
  const passwordStrengthText = passwordStrength === "empty" ? null : passwordStrengthLabels[passwordStrength];

  useEffect(() => {
    // Get token from URL
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (!token) {
      setTokenError("No invitation token provided");
      setVerifyingToken(false);
      return;
    }

    setInvitationToken(token);

    // Verify token with backend
    const verifyToken = async () => {
      try {
        const response = await fetch(`/api/invitations/verify/${token}`);
        if (!response.ok) {
          const error = await response.json();
          setTokenError(error.error || "Invalid invitation");
          setVerifyingToken(false);
          return;
        }

        const data = await response.json();
        setInvitationData(data);
        if (data?.country) setCountry(data.country);
        if (data?.sector) setSector(data.sector);
        setVerifyingToken(false);
      } catch (error) {
        setTokenError("Failed to verify invitation");
        setVerifyingToken(false);
      }
    };

    verifyToken();
  }, []);

  // Fetch privacy policy
  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      try {
        const response = await fetch("/privacy-policy.md");
        if (response.ok) {
          const text = await response.text();
          setPrivacyPolicyContent(text);
        }
      } catch (error) {
        console.error("Failed to fetch privacy policy:", error);
      }
    };
    fetchPrivacyPolicy();
  }, []);

  const handleComplete = async () => {
    // Validation
    if (!email || !password) {
      toast({
        title: "Required fields",
        description: "Please provide your email and password.",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters.",
        variant: "destructive",
      });
      return;
    }

    if (isWorkspaceCreation && !sector) {
      toast({
        title: "Required fields",
        description: "*The pilot is currently optimized for these three sectors.",
        variant: "destructive",
      });
      return;
    }

    if (!acceptPolicy) {
      toast({
        title: "Consent required",
        description: "Please accept the policy to continue.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email,
          password,
          firstName,
          lastName,
          invitationToken,
          sector,
          country,
          acceptPolicy: true,
          policyVersion,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Signup failed");
      }

      const data = await response.json();

      toast({
        title: "Welcome to 180Pi!",
        description: isWorkspaceCreation
          ? "Your workspace has been created successfully."
          : "You've joined your workspace successfully.",
      });

      // Redirect to dashboard
      setLocation("/dashboard");
    } catch (error) {
      toast({
        title: "Signup failed",
        description: error instanceof Error ? error.message : "An error occurred during signup",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (verifyingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-lg"
        >
          <Card className="border-destructive shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                {"Invalid Invitation"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{tokenError}</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (!invitationData) {
    return null;
  }

  return (
    <div
      className="min-h-screen flex flex-col relative"
      style={{
        backgroundImage: `url(${bgDecorative})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Language switcher â€” top-right */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher />
      </div>

      {/* Main Content â€” Two Panel Layout */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-[1024px] max-w-[1024px] bg-white rounded-3xl shadow-[0px_8px_10px_-6px_rgba(0,0,0,0.10)] shadow-xl outline outline-1 outline-offset-[-1px] outline-slate-200 inline-flex justify-start items-start overflow-hidden">
          <div className="flex-1 self-stretch p-16 inline-flex flex-col justify-start items-start gap-8">
            <div className="self-stretch flex flex-col justify-start items-start gap-2">
              <div className="self-stretch justify-center text-primary text-3xl font-bold font-['Inter'] leading-9">
                {"Company Details"}
              </div>
              <div className="self-stretch justify-center text-slate-500 text-base font-normal font-['Inter'] leading-6">
                {"Set up your workspace to start tracking sustainability metrics"}
              </div>
            </div>

            <form
              className="self-stretch pt-2 flex flex-col justify-start items-start gap-5"
              onSubmit={(e) => {
                e.preventDefault();
                void handleComplete();
              }}
            >
              <div className="self-stretch flex flex-col justify-start items-start gap-2">
                <Label htmlFor="company" className="text-slate-700 text-sm font-medium font-['Inter'] leading-5">
                  {"Company Name"}
                </Label>
                <Input
                  id="company"
                  value={invitationData.companyName}
                  readOnly
                  disabled
                  className="h-12 rounded-2xl border-slate-200 bg-slate-50 px-4 text-base text-slate-500 shadow-none disabled:cursor-not-allowed disabled:opacity-100"
                />
              </div>

              <div className="self-stretch flex flex-col justify-start items-start gap-2">
                <Label htmlFor="email" className="text-slate-700 text-sm font-medium font-['Inter'] leading-5">
                  {"Email Address"}
                </Label>
                <div className="self-stretch relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    disabled={isLoading}
                    placeholder={"your.email@company.com"}
                    className="h-12 rounded-2xl border-slate-200 bg-white pl-10 pr-4 text-base text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-primary"
                  />
                </div>
              </div>

              <div className="self-stretch inline-flex justify-center items-start gap-4">
                <div className="flex-1 self-stretch inline-flex flex-col justify-start items-start gap-2">
                  <Label htmlFor="firstName" className="text-slate-700 text-sm font-medium font-['Inter'] leading-5">
                    {"First Name"}
                  </Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                    disabled={isLoading}
                    placeholder={"John"}
                    className="h-12 rounded-2xl border-slate-200 bg-white px-4 text-base text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-primary"
                  />
                </div>
                <div className="flex-1 self-stretch inline-flex flex-col justify-start items-start gap-2">
                  <Label htmlFor="lastName" className="text-slate-700 text-sm font-medium font-['Inter'] leading-5">
                    {"Last Name"}
                  </Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                    disabled={isLoading}
                    placeholder={"Doe"}
                    className="h-12 rounded-2xl border-slate-200 bg-white px-4 text-base text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-primary"
                  />
                </div>
              </div>

              <div className="self-stretch flex flex-col justify-start items-start gap-2">
                <Label htmlFor="password" className="text-slate-700 text-sm font-medium font-['Inter'] leading-5">
                  {"Password"}
                </Label>
                <div className="self-stretch relative">
                  <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    disabled={isLoading}
                    placeholder={"At least 6 characters"}
                    className="h-12 rounded-2xl border-slate-200 bg-white pl-10 pr-4 text-base text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-primary"
                  />
                </div>
                <div className="self-stretch inline-flex justify-center items-start gap-1">
                  {[0, 1, 2, 3].map((index) => (
                    <div
                      key={index}
                      className={`flex-1 h-1 rounded-full ${
                        passwordStrengthMeta && index < passwordStrengthMeta.segments
                          ? passwordStrengthMeta.color
                          : "bg-slate-200"
                      }`}
                    />
                  ))}
                </div>
                <div className="self-stretch text-slate-500 text-xs font-normal font-['Inter'] leading-4">
                  {passwordStrength === "empty"
                    ? "Use 8+ characters with a mix of letters and symbols."
                    : `${"Strength"}: ${passwordStrengthText}`}
                </div>
              </div>

              <div className="self-stretch inline-flex justify-center items-start gap-4">
                <div className="flex-1 self-stretch inline-flex flex-col justify-start items-start gap-2">
                  <Label htmlFor="country" className="text-slate-700 text-sm font-medium font-['Inter'] leading-5">
                    {"Country"}
                  </Label>
                  <div className="self-stretch relative">
                    <Globe className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <CountryCombobox
                      value={country}
                      onValueChange={setCountry}
                      placeholder={"Select country"}
                      disabled={isLoading || !isWorkspaceCreation}
                      className="h-12 rounded-2xl border-slate-200 bg-white pl-10 pr-4 text-base text-slate-900 shadow-none"
                    />
                  </div>
                </div>
                <div className="flex-1 self-stretch inline-flex flex-col justify-start items-start gap-2">
                  <Label htmlFor="sector" className="text-slate-700 text-sm font-medium font-['Inter'] leading-5">
                    {"Sector"}
                  </Label>
                  <div className="self-stretch relative">
                    <Briefcase className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Select value={sector} onValueChange={setSector} disabled={isLoading || !isWorkspaceCreation}>
                      <SelectTrigger id="sector" className="h-12 rounded-2xl border-slate-200 bg-white pl-10 pr-4 text-base text-slate-900 shadow-none">
                        <SelectValue placeholder={"Select primary sector"} />
                      </SelectTrigger>
                      <SelectContent className="max-h-60 overflow-y-auto">
                        {naceOptions.map((option) => (
                          <SelectItem key={option.code} value={option.code}>
                            {option.code} - {option.translatedLabel}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {invitationData.portfolioAccess && (
                <div className="self-stretch p-4 bg-primary/10 rounded-2xl flex items-start gap-3">
                  <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-sm font-normal font-['Inter'] leading-6 text-slate-700">
                    {"Invited by "}
                    <strong className="font-semibold text-slate-900">{invitationData.portfolioAccess.managerName}</strong>
                    {invitationData.portfolioAccess.organization && (
                      <>
                        {", "}
                        <strong className="font-semibold text-slate-900">{invitationData.portfolioAccess.organization}</strong>
                      </>
                    )}
                    {". They will have read-only access to your assessments, supporting documents and final reports."}
                  </p>
                </div>
              )}

              <div className="self-stretch flex items-start gap-3 pt-1">
                <Checkbox
                  id="acceptPolicy"
                  checked={acceptPolicy}
                  onCheckedChange={(checked) => setAcceptPolicy(checked === true)}
                  disabled={isLoading}
                  className="mt-1"
                />
                {invitationData.portfolioAccess ? (
                  <div className="text-sm font-normal font-['Inter'] leading-6 text-slate-600">
                    {"I accept the"}{" "}
                    <button
                      type="button"
                      onClick={() => setPolicyModalOpen(true)}
                      className="font-medium text-primary underline underline-offset-2"
                    >
                      {"data policy"}
                    </button>{" "}
                    {"and this access."}
                  </div>
                ) : (
                  <div className="text-sm font-normal font-['Inter'] leading-6 text-slate-600">
                    {"I agree to the applicable"}{" "}
                    <button
                      type="button"
                      onClick={() => setPolicyModalOpen(true)}
                      className="font-medium text-primary underline underline-offset-2"
                    >
                      {"privacy policy and terms"}
                    </button>{" "}
                    {`I understand my data will be stored securely. (version ${policyVersion}).`}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="self-stretch h-14 rounded-2xl bg-primary px-6 text-base font-bold font-['Inter'] leading-6 text-white shadow-[0px_10px_15px_-3px_rgba(0,68,76,0.20)] hover:bg-primary/90"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {"Creating Account..."}
                  </>
                ) : (
                  <>
                    {isWorkspaceCreation ? "Create Account & Workspace" : "Create Account & Join Workspace"}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </div>
          <div className="w-96 self-stretch p-12 relative bg-primary inline-flex flex-col justify-center items-start overflow-hidden">
            <div data-svg-wrapper className="left-0 top-0 absolute">
              <svg width="400" height="940" viewBox="0 0 400 940" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.2">
                  <g filter="url(#filter0_f_0_199)">
                    <rect x="184" y="-102.14" width="256" height="256" rx="128" fill="white" />
                  </g>
                  <g filter="url(#filter1_f_0_199)">
                    <rect x="-80" y="822.8" width="320" height="320" rx="160" fill="#94A3B8" />
                  </g>
                </g>
                <defs>
                  <filter id="filter0_f_0_199" x="120" y="-166.14" width="384" height="384" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                    <feGaussianBlur stdDeviation="32" result="effect1_foregroundBlur_0_199" />
                  </filter>
                  <filter id="filter1_f_0_199" x="-144" y="758.8" width="448" height="448" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                    <feGaussianBlur stdDeviation="32" result="effect1_foregroundBlur_0_199" />
                  </filter>
                </defs>
              </svg>
            </div>
            <div data-svg-wrapper className="left-[160px] top-[706.83px] absolute">
              <svg width="200" height="192" viewBox="0 0 200 192" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.1">
                  <path d="M50 191.667C43.0556 191.667 37.1528 189.236 32.2917 184.375C27.4306 179.514 25 173.611 25 166.667C25 159.722 27.4306 153.819 32.2917 148.958C37.1528 144.097 43.0556 141.667 50 141.667C51.9444 141.667 53.75 141.875 55.4167 142.292C57.0833 142.708 58.6806 143.264 60.2083 143.958L72.0833 129.167C68.1944 124.861 65.4861 120 63.9583 114.583C62.4306 109.167 62.0833 103.75 62.9167 98.3333L46.0417 92.7083C43.6806 96.1806 40.6944 98.9583 37.0833 101.042C33.4722 103.125 29.4444 104.167 25 104.167C18.0556 104.167 12.1528 101.736 7.29167 96.875C2.43056 92.0139 0 86.1111 0 79.1667C0 72.2222 2.43056 66.3194 7.29167 61.4583C12.1528 56.5972 18.0556 54.1667 25 54.1667C31.9444 54.1667 37.8472 56.5972 42.7083 61.4583C47.5694 66.3194 50 72.2222 50 79.1667C50 79.4444 50 79.7222 50 80C50 80.2778 50 80.5556 50 80.8333L66.875 86.6667C69.6528 81.6667 73.3681 77.4306 78.0208 73.9583C82.6736 70.4861 87.9167 68.2639 93.75 67.2917V49.1667C88.3333 47.6389 83.8542 44.6875 80.3125 40.3125C76.7708 35.9375 75 30.8333 75 25C75 18.0556 77.4306 12.1528 82.2917 7.29167C87.1528 2.43056 93.0556 0 100 0C106.944 0 112.847 2.43056 117.708 7.29167C122.569 12.1528 125 18.0556 125 25C125 30.8333 123.194 35.9375 119.583 40.3125C115.972 44.6875 111.528 47.6389 106.25 49.1667V67.2917C112.083 68.2639 117.326 70.4861 121.979 73.9583C126.632 77.4306 130.347 81.6667 133.125 86.6667L150 80.8333C150 80.5556 150 80.2778 150 80C150 79.7222 150 79.4444 150 79.1667C150 72.2222 152.431 66.3194 157.292 61.4583C162.153 56.5972 168.056 54.1667 175 54.1667C181.944 54.1667 187.847 56.5972 192.708 61.4583C197.569 66.3194 200 72.2222 200 79.1667C200 86.1111 197.569 92.0139 192.708 96.875C187.847 101.736 181.944 104.167 175 104.167C170.556 104.167 166.493 103.125 162.812 101.042C159.132 98.9583 156.181 96.1806 153.958 92.7083L137.083 98.3333C137.917 103.75 137.569 109.132 136.042 114.479C134.514 119.826 131.806 124.722 127.917 129.167L139.792 143.75C141.319 143.056 142.917 142.535 144.583 142.188C146.25 141.84 148.056 141.667 150 141.667C156.944 141.667 162.847 144.097 167.708 148.958C172.569 153.819 175 159.722 175 166.667C175 173.611 172.569 179.514 167.708 184.375C162.847 189.236 156.944 191.667 150 191.667C143.056 191.667 137.153 189.236 132.292 184.375C127.431 179.514 125 173.611 125 166.667C125 163.889 125.451 161.215 126.354 158.646C127.257 156.076 128.472 153.75 130 151.667L118.125 136.875C112.431 140.069 106.354 141.667 99.8958 141.667C93.4375 141.667 87.3611 140.069 81.6667 136.875L70 151.667C71.5278 153.75 72.7431 156.076 73.6458 158.646C74.5486 161.215 75 163.889 75 166.667C75 173.611 72.5694 179.514 67.7083 184.375C62.8472 189.236 56.9444 191.667 50 191.667V191.667M25 87.5C27.3611 87.5 29.3403 86.7014 30.9375 85.1042C32.5347 83.5069 33.3333 81.5278 33.3333 79.1667C33.3333 76.8056 32.5347 74.8264 30.9375 73.2292C29.3403 71.6319 27.3611 70.8333 25 70.8333C22.6389 70.8333 20.6597 71.6319 19.0625 73.2292C17.4653 74.8264 16.6667 76.8056 16.6667 79.1667C16.6667 81.5278 17.4653 83.5069 19.0625 85.1042C20.6597 86.7014 22.6389 87.5 25 87.5V87.5M50 175C52.3611 175 54.3403 174.201 55.9375 172.604C57.5347 171.007 58.3333 169.028 58.3333 166.667C58.3333 164.306 57.5347 162.326 55.9375 160.729C54.3403 159.132 52.3611 158.333 50 158.333C47.6389 158.333 45.6597 159.132 44.0625 160.729C42.4653 162.326 41.6667 164.306 41.6667 166.667C41.6667 169.028 42.4653 171.007 44.0625 172.604C45.6597 174.201 47.6389 175 50 175V175M100 33.3333C102.361 33.3333 104.34 32.5347 105.938 30.9375C107.535 29.3403 108.333 27.3611 108.333 25C108.333 22.6389 107.535 20.6597 105.938 19.0625C104.34 17.4653 102.361 16.6667 100 16.6667C97.6389 16.6667 95.6597 17.4653 94.0625 19.0625C92.4653 20.6597 91.6667 22.6389 91.6667 25C91.6667 27.3611 92.4653 29.3403 94.0625 30.9375C95.6597 32.5347 97.6389 33.3333 100 33.3333V33.3333M100 125C105.833 125 110.764 122.986 114.792 118.958C118.819 114.931 120.833 110 120.833 104.167C120.833 98.3333 118.819 93.4028 114.792 89.375C110.764 85.3472 105.833 83.3333 100 83.3333C94.1667 83.3333 89.2361 85.3472 85.2083 89.375C81.1806 93.4028 79.1667 98.3333 79.1667 104.167C79.1667 110 81.1806 114.931 85.2083 118.958C89.2361 122.986 94.1667 125 100 125V125M150 175C152.361 175 154.34 174.201 155.938 172.604C157.535 171.007 158.333 169.028 158.333 166.667C158.333 164.306 157.535 162.326 155.938 160.729C154.34 159.132 152.361 158.333 150 158.333C147.639 158.333 145.66 159.132 144.062 160.729C142.465 162.326 141.667 164.306 141.667 166.667C141.667 169.028 142.465 171.007 144.062 172.604C145.66 174.201 147.639 175 150 175V175M175 87.5C177.361 87.5 179.34 86.7014 180.938 85.1042C182.535 83.5069 183.333 81.5278 183.333 79.1667C183.333 76.8056 182.535 74.8264 180.938 73.2292C179.34 71.6319 177.361 70.8333 175 70.8333C172.639 70.8333 170.66 71.6319 169.062 73.2292C167.465 74.8264 166.667 76.8056 166.667 79.1667C166.667 81.5278 167.465 83.5069 169.062 85.1042C170.66 86.7014 172.639 87.5 175 87.5V87.5M100 25V25V25V25V25V25V25V25V25V25M25 79.1667V79.1667V79.1667V79.1667V79.1667V79.1667V79.1667V79.1667V79.1667V79.1667M100 104.167V104.167V104.167V104.167V104.167V104.167V104.167V104.167V104.167V104.167M175 79.1667V79.1667V79.1667V79.1667V79.1667V79.1667V79.1667V79.1667V79.1667V79.1667M50 166.667V166.667V166.667V166.667V166.667V166.667V166.667V166.667V166.667V166.667M150 166.667V166.667V166.667V166.667V166.667V166.667V166.667V166.667V166.667V166.667" fill="white" />
                </g>
              </svg>
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-8">
              <div className="w-36 h-16 relative bg-white/20 rounded-2xl backdrop-blur-[6px]">
                <img className="w-20 h-8 left-[33px] top-[15.50px] absolute object-contain brightness-0 invert" src={logo} alt="180Pi" />
              </div>
              <div className="self-stretch flex flex-col justify-start items-start gap-4">
                <div className="self-stretch pb-[0.75px] flex flex-col justify-start items-start">
                  <div className="w-80 justify-center text-white text-3xl font-bold font-['Inter'] leading-9">{"Build smarter climate investments."}</div>
                </div>
                <div className="self-stretch flex flex-col justify-start items-start">
                  <div className="self-stretch justify-center text-white text-lg font-normal font-['Inter'] leading-7">{"Join a growing network of organizations using data-driven ESG insights to understand climate impact and guide sustainable investment decisions."}</div>
                </div>
              </div>
              <div className="self-stretch pt-8 pb-12 flex flex-col justify-start items-start gap-6">
                <div className="self-stretch inline-flex justify-start items-center gap-4">
                  <div className="w-10 h-10 shrink-0 bg-white/10 rounded-full flex justify-center items-center">
                    <Check className="h-4 w-4 text-white" strokeWidth={3} />
                  </div>
                  <div className="w-60 inline-flex flex-col justify-start items-start">
                    <div className="w-60 h-6 justify-start text-white text-base font-medium font-['Inter'] leading-6">{"ESG transparency and impact tracking"}</div>
                  </div>
                </div>
                <div className="self-stretch inline-flex justify-start items-center gap-4">
                  <div className="w-10 h-10 shrink-0 bg-white/10 rounded-full flex justify-center items-center">
                    <Check className="h-4 w-4 text-white" strokeWidth={3} />
                  </div>
                  <div className="w-60 inline-flex flex-col justify-start items-start">
                    <div className="self-stretch h-6 justify-start text-white text-base font-medium font-['Inter'] leading-6">{"Climate investment simulation tools"}</div>
                  </div>
                </div>
                <div className="self-stretch inline-flex justify-start items-center gap-4">
                  <div className="w-10 h-10 shrink-0 bg-white/10 rounded-full flex justify-center items-center">
                    <Check className="h-4 w-4 text-white" strokeWidth={3} />
                  </div>
                  <div className="w-60 inline-flex flex-col justify-start items-start">
                    <div className="self-stretch h-6 justify-start text-white text-base font-medium font-['Inter'] leading-6">{"Data insights for SMEs and investors"}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Privacy Policy Modal */}
      <Dialog open={policyModalOpen} onOpenChange={setPolicyModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{"Privacy Policy and Terms"}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[50vh] pr-4">
            <div className="policy-content text-sm space-y-4 [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-4 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-6 [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-4 [&_p]:text-muted-foreground [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_ul]:text-muted-foreground [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1 [&_ol]:text-muted-foreground [&_li]:text-muted-foreground [&_strong]:text-foreground [&_strong]:font-semibold [&_blockquote]:border-l-2 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_hr]:my-4 [&_hr]:border-border [&_table]:w-full [&_table]:text-sm [&_th]:text-left [&_th]:font-semibold [&_th]:p-2 [&_th]:border-b [&_td]:p-2 [&_td]:border-b [&_td]:text-muted-foreground">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {privacyPolicyContent}
              </ReactMarkdown>
            </div>
          </ScrollArea>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setPolicyModalOpen(false)}
            >
              {"Close"}
            </Button>
            <Button
              onClick={() => {
                setAcceptPolicy(true);
                setPolicyModalOpen(false);
              }}
            >
              {"Accept"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div> 
  ); 
}

